/* AccommodationsToolbar.js — v6.34.18

   Renders WIDA-recognized accommodation widgets driven by the
   ``ui_flags`` block + override fields the v6.32.152 backend
   attaches to every question when the exam session has any
   accommodation baked.

   Widgets in scope of this toolbar:
     - RA  Read Aloud         → inline <audio> player (when audio_url is present).
     - ES  Extended Time      → amber info banner.                           (legacy alias of ET)
     - WD  Word-to-Word Dict. → "Open Glossary" button + side-panel.
     - LP  Large Print        → chip; transform applied by .lp-mode on root  (v6.34.1)
     - HC  High Contrast      → chip; transform applied by .hc-mode on root  (v6.34.1)
     - ET  Extended Time (1.5×) → chip                                       (v6.34.18)
     - RP  Repeat Audio       → per-item replay counter; default 2 plays max (v6.34.18)
     - MA  Manual Audio Ctrl  → no auto-play; student presses Play           (v6.34.18)
     - LM  Line Reader/Mask   → toggle overlay focusing only current item    (v6.34.18)

   HL (Highlighter) is intentionally NOT rendered here — StudentExam.js
   already wires the standalone <Highlighter> component for reading +
   writing modalities under the v6.32.114 hasHL flag. Duplicating that
   widget would confuse students.

   Self-contained: every widget renders only when the matching ui_flags
   key is true. Safe to render unconditionally on every question — when
   ui_flags is missing (legacy session-less call) the component returns
   null.
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Volume2, Clock, BookOpen, X, ZoomIn, Contrast, Play, Pause, Repeat, ScanLine, Hourglass } from "lucide-react";
import { Button } from "../components/ui/button";

const HIGHLIGHT_CLASS = "mosaic-hl-mark";  // reserved for future use; HL handled by standalone <Highlighter />

function GlossaryPanel({ question, onClose }) {
  // Variant override fields convention: glossary_<lang> (e.g., glossary_es, glossary_zh).
  const entries = useMemo(() => {
    const out = [];
    for (const [k, v] of Object.entries(question || {})) {
      if (!k.startsWith("glossary_") || !v || typeof v !== "object") continue;
      const lang = k.slice("glossary_".length);
      for (const [word, gloss] of Object.entries(v)) {
        out.push({ lang, word, gloss });
      }
    }
    return out;
  }, [question]);

  return (
    <aside
      className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col"
      data-testid="accom-glossary-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-emerald-50">
        <div className="flex items-center gap-2">
          <BookOpen size={18} className="text-emerald-700" />
          <span className="font-bold text-emerald-900">Glossary (WD)</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-800"
          data-testid="accom-glossary-close"
          aria-label="Close glossary"
        >
          <X size={18} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 text-sm">
        {entries.length === 0 ? (
          <p className="text-gray-500 italic">
            No glossary entries are available for this question. Your
            accommodation is still active — ask your teacher if you need
            help with a specific word.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="accom-glossary-entries">
            {entries.map((e, i) => (
              <li key={`${e.lang}-${e.word}-${i}`} className="border-b border-gray-100 pb-2">
                <div className="font-bold text-gray-900">{e.word}</div>
                <div className="text-xs uppercase tracking-wide text-gray-500">{e.lang}</div>
                <div className="text-gray-700 mt-0.5">{e.gloss}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

export default function AccommodationsToolbar({ question }) {
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const audioRef = useRef(null);
  // v6.34.18 — Repeat Audio replay counter + Line Reader mask toggle.
  // Replays are capped at REPEAT_AUDIO_MAX_PLAYS per item to mirror the
  // WIDA ACCESS digital RP accommodation (1 student-initiated replay
  // beyond the first auto-play = 2 total).
  const REPEAT_AUDIO_MAX_PLAYS = 2;
  const [playsUsed, setPlaysUsed] = useState(0);
  const [maskOn, setMaskOn] = useState(false);

  const ui = question?.ui_flags || {};
  const showRA = !!ui.read_aloud;
  const showES = !!ui.extra_time;
  const showWD = !!ui.dictionary;
  const showLP = !!ui.large_print;     // v6.34.1
  const showHC = !!ui.high_contrast;   // v6.34.1
  const showET = !!ui.extended_time;   // v6.34.18
  const showRP = !!ui.repeat_audio;    // v6.34.18
  const showMA = !!ui.manual_audio;    // v6.34.18
  const showLM = !!ui.line_reader;     // v6.34.18

  // Reset glossary state + replay counter + mask whenever the question
  // changes so the side panel + counters don't linger across navigation.
  useEffect(() => {
    setGlossaryOpen(false);
    setPlaysUsed(0);
    setMaskOn(false);
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch { /* noop */ }
    }
  }, [question?.id]);

  // v6.34.18 — Toggle the body-scoped .lm-mask class so the Line-Reader
  // overlay shows / hides without requiring StudentExam.js to know
  // about it. The CSS class is defined in frontend/src/index.css.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (maskOn) {
      document.body.classList.add("lm-mask");
    } else {
      document.body.classList.remove("lm-mask");
    }
    return () => {
      try { document.body.classList.remove("lm-mask"); } catch { /* noop */ }
    };
  }, [maskOn]);

  if (!showRA && !showES && !showWD && !showLP && !showHC && !showET && !showRP && !showMA && !showLM) return null;

  const repeatBlocked = showRP && playsUsed >= REPEAT_AUDIO_MAX_PLAYS;
  const onAudioPlay = () => {
    // Count a play only on the leading edge (when audio actually starts).
    setPlaysUsed((n) => n + 1);
  };
  const onManualPlay = () => {
    if (audioRef.current) {
      try { audioRef.current.play(); } catch { /* noop */ }
    }
  };
  const onManualPause = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
    }
  };

  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 px-3 py-2"
      data-testid="accommodations-toolbar"
    >
      <span className="text-[10px] font-bold tracking-wide text-amber-800 uppercase">
        Accommodations active:
      </span>

      {showRA && question?.audio_url && (
        <div className="inline-flex items-center gap-2" data-testid="accom-ra-player">
          <Volume2 size={14} className="text-amber-700" />
          <span className="text-xs font-medium text-amber-900">Read Aloud</span>
          <audio
            ref={audioRef}
            controls
            preload="none"
            src={question.audio_url}
            className="h-8"
            data-testid="accom-ra-audio"
          />
        </div>
      )}
      {showRA && !question?.audio_url && (
        <span
          className="inline-flex items-center gap-1 text-xs text-amber-900 italic"
          data-testid="accom-ra-no-audio"
          title="Read Aloud is granted but no audio variant exists for this question yet — your teacher may read it aloud."
        >
          <Volume2 size={14} /> Read Aloud (teacher-led)
        </span>
      )}

      {showES && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium"
          data-testid="accom-es-banner"
          title="Extended Time accommodation is in effect for this exam."
        >
          <Clock size={14} /> Extended Time
        </span>
      )}

      {showWD && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setGlossaryOpen((o) => !o)}
          className="h-8 border-emerald-400 text-emerald-800 hover:bg-emerald-50"
          data-testid="accom-wd-open"
        >
          <BookOpen size={14} className="mr-1" />
          {glossaryOpen ? "Close Glossary" : "Open Glossary"}
        </Button>
      )}

      {showWD && glossaryOpen && (
        <GlossaryPanel question={question} onClose={() => setGlossaryOpen(false)} />
      )}

      {/* v6.34.1 — Large Print chip. The visual transform is applied by the
          .lp-mode class on the exam root in StudentExam.js; this chip just
          surfaces the active accommodation so the student + proctor know
          it's on. */}
      {showLP && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium"
          data-testid="accom-lp-chip"
          title="Large Print accommodation is active — exam text rendered at 125% size."
        >
          <ZoomIn size={14} /> Large Print
        </span>
      )}

      {/* v6.34.1 — High Contrast chip. The visual transform is applied by
          the .hc-mode class on the exam root in StudentExam.js. */}
      {showHC && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium"
          data-testid="accom-hc-chip"
          title="High Contrast accommodation is active — black background, bold white text, yellow focus rings."
        >
          <Contrast size={14} /> High Contrast
        </span>
      )}

      {/* v6.34.18 — Extended Time (ET). Distinct from ES; ET communicates
          a 1.5× duration multiplier and is surfaced as an explicit chip so
          proctors can verify the accommodation is active before the timer
          enforcement layer is wired (current exam UI is untimed). */}
      {showET && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium"
          data-testid="accom-et-chip"
          title="Extended Time — 1.5× the standard duration is allowed for this student (WIDA ET, v6.34.18)."
        >
          <Hourglass size={14} /> Extended Time (1.5×)
        </span>
      )}

      {/* v6.34.18 — Repeat Audio (RP). Standalone chip surfaces the
          accommodation even when no audio_url exists on the item (RP is
          only enforceable when both RP and RA are granted; this chip is
          informational so the proctor can verify). */}
      {showRP && !question?.audio_url && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium"
          data-testid="accom-rp-chip"
          title="Repeat Audio — up to 2 plays per Listening item are allowed for this student (WIDA RP, v6.34.18)."
        >
          <Repeat size={14} /> Repeat Audio
        </span>
      )}

      {/* v6.34.18 — Manual Audio Control (MA). Standalone chip when no
          audio_url exists for the current item. */}
      {showMA && !question?.audio_url && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium"
          data-testid="accom-ma-chip"
          title="Manual Audio Control — student-paced audio playback (no auto-play). (WIDA MA, v6.34.18)"
        >
          <Play size={14} /> Manual Audio
        </span>
      )}

      {/* v6.34.18 — Line Reader / Masking (LM). Toggle button — when ON,
          the .lm-mask class is set on document.body (CSS dims everything
          except the active question card). Off by default; student opts
          in per their own preference. */}
      {showLM && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setMaskOn((m) => !m)}
          className="h-8 border-indigo-400 text-indigo-800 hover:bg-indigo-50"
          data-testid="accom-lm-toggle"
          aria-pressed={maskOn}
          title="Line Reader / Masking — focus the screen on this question only (WIDA LM, v6.34.18)."
        >
          <ScanLine size={14} className="mr-1" />
          {maskOn ? "Mask: ON" : "Mask: OFF"}
        </Button>
      )}
    </div>
  );
}
